import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getLicenses, createLicense, deleteLicense, getDrones } from '../api'
import './Licenses.css'

// ─── Types ───────────────────────────────────────────────────────────────────

interface License {
  id: string
  license_key: string
  drone_uuid?: string
  drone_name?: string
  tail_number?: string
  is_active: boolean
  issued_at?: string
}

interface Drone {
  id: string
  drone_id: string
  tail_number?: string
  drone_model?: string
  status: string
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDate(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('he-IL', {
    year: 'numeric', month: '2-digit', day: '2-digit',
  })
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function Licenses() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [selectedDrone, setSelectedDrone] = useState('')
  const [tailOverride, setTailOverride] = useState('')
  const [search, setSearch] = useState('')
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  // ─── Data ────────────────────────────────────────────────────────────────
  const { data: licenses = [], isLoading } = useQuery<License[]>({
    queryKey: ['licenses'],
    queryFn: getLicenses,
  })

  const { data: drones = [] } = useQuery<Drone[]>({
    queryKey: ['drones'],
    queryFn: getDrones,
  })

  // ─── Auto-fill tail number when drone changes ─────────────────────────────
  const handleDroneChange = (droneId: string) => {
    setSelectedDrone(droneId)
    const drone = (drones as Drone[]).find(d => d.id === droneId)
    setTailOverride(drone?.tail_number ?? '')
  }

  // ─── Mutations ────────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: createLicense,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licenses'] })
      // Stay on page — just reset form
      setSelectedDrone('')
      setTailOverride('')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteLicense,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licenses'] })
      setConfirmDelete(null)
    },
  })

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleGenerate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedDrone) return
    createMutation.mutate({
      drone_uuid: selectedDrone,
      tail_number: tailOverride.trim() || undefined,
    })
  }

  // ─── Filtered list ────────────────────────────────────────────────────────
  const filtered = (licenses as License[]).filter(l => {
    if (!search) return true
    const q = search.toLowerCase()
    return (
      l.license_key.toLowerCase().includes(q) ||
      (l.drone_name ?? '').toLowerCase().includes(q) ||
      (l.tail_number ?? '').toLowerCase().includes(q)
    )
  })

  if (isLoading) return <div className="page-loading">טוען...</div>

  return (
    <div className="licenses-page">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="lic-header">
        <div className="lic-header-left">
          <button className="btn-back-sm" onClick={() => navigate('/settings')}>
            ← הגדרות
          </button>
          <h1>🔑 רישיונות</h1>
        </div>
        <span className="serial-count">{(licenses as License[]).length} רישיונות</span>
      </div>

      {/* ── Generate form ────────────────────────────────────────────────── */}
      <div className="serial-form-card">
        <p className="lic-section-hint" style={{ marginBottom: '1rem' }}>
          בחר רחפן ומספר זנב — המערכת תפיק מזהה רישיון בפורמט <strong>XX-DDMMYY-DR-01</strong>.
        </p>

        <form onSubmit={handleGenerate} className="license-gen-form">
          {/* Drone selector */}
          <div className="sf-row">
            <label>רחפן <span className="req">*</span></label>
            <select
              value={selectedDrone}
              onChange={e => handleDroneChange(e.target.value)}
              required
            >
              <option value="">— בחר רחפן —</option>
              {(drones as Drone[]).map(d => (
                <option key={d.id} value={d.id}>
                  {d.drone_id}{d.drone_model ? ` (${d.drone_model})` : ''}
                  {d.tail_number ? ` · ${d.tail_number}` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Tail number (auto-filled, editable) */}
          <div className="sf-row">
            <label>מספר זנב <span className="opt">— ממולא אוטומטית, ניתן לשינוי</span></label>
            <input
              value={tailOverride}
              onChange={e => setTailOverride(e.target.value)}
              placeholder="לדוגמה: 4X-ABC"
            />
          </div>

          {/* Preview */}
          {selectedDrone && (
            <div className="sf-preview" style={{ marginBottom: '0.2rem' }}>
              תצוגה מקדימה:{' '}
              <strong>
                {(tailOverride || (() => {
                  const d = (drones as Drone[]).find(x => x.id === selectedDrone)
                  return d?.tail_number || d?.drone_id || 'XX'
                })())
                  .replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 2) || 'XX'}
                -{new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }).replace(/\//g, '')}
                -DR-01
              </strong>
            </div>
          )}

          <div className="sf-actions">
            <button
              type="submit"
              className="btn-primary"
              disabled={!selectedDrone || createMutation.isPending}
            >
              {createMutation.isPending ? '⏳ מפיק...' : '⚡ הפק רישיון'}
            </button>
          </div>

          {createMutation.isError && (
            <div className="form-error">{(createMutation.error as Error).message}</div>
          )}
          {createMutation.isSuccess && (
            <div className="form-success">✅ רישיון הופק בהצלחה!</div>
          )}
        </form>
      </div>

      {/* ── Search bar ───────────────────────────────────────────────────── */}
      <div className="serial-search-bar">
        <input
          className="serial-search"
          placeholder="🔍 חיפוש לפי מזהה, רחפן, מספר זנב..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <span className="serial-count">{filtered.length} תוצאות</span>
      </div>

      {/* ── Table ────────────────────────────────────────────────────────── */}
      {filtered.length === 0 ? (
        <div className="serial-empty">
          <div className="serial-empty-icon">🔑</div>
          <p>אין רישיונות עדיין.</p>
          <p>בחר רחפן ולחץ <strong>⚡ הפק רישיון</strong> כדי ליצור את הראשון.</p>
        </div>
      ) : (
        <div className="serial-table-wrap">
          <table className="serial-table">
            <thead>
              <tr>
                <th>#</th>
                <th>רחפן</th>
                <th>מספר זנב</th>
                <th>מזהה רישיון</th>
                <th>תאריך הפקה</th>
                <th>סטטוס</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l, idx) => (
                <tr key={l.id}>
                  <td className="s-idx">{idx + 1}</td>
                  <td className="s-type-lbl">{l.drone_name ?? '—'}</td>
                  <td className="s-mono">{l.tail_number ?? '—'}</td>
                  <td><code className="s-serial">{l.license_key}</code></td>
                  <td className="s-date">{fmtDate(l.issued_at)}</td>
                  <td>
                    <span className={`days-badge ${l.is_active ? 'good' : 'warn'}`}>
                      {l.is_active ? 'פעיל' : 'לא פעיל'}
                    </span>
                  </td>
                  <td>
                    {confirmDelete === l.id ? (
                      <>
                        <button
                          className="btn-danger-xs"
                          onClick={() => deleteMutation.mutate(l.id)}
                          disabled={deleteMutation.isPending}
                        >
                          אשר מחיקה
                        </button>
                        <button
                          className="btn-ghost-xs"
                          onClick={() => setConfirmDelete(null)}
                        >
                          ביטול
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn-ghost-xs"
                        onClick={() => setConfirmDelete(l.id)}
                      >
                        🗑
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
